import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type Event, nip19 } from 'nostr-tools';
import { Heart, MessageCircle, Repeat2, Share, MoreHorizontal, Coins, User, VolumeX, Volume2, Flag, BarChart3, CornerDownRight, Pencil, Trash2, X, Send, Quote, Rocket, Bookmark, Copy, Share2, QrCode, Check, ShieldAlert, Languages, Pin, Image as ImageIcon, Loader2, Tag, Eye } from 'lucide-react';
import CrossPostModal from './CrossPostModal';
import NoteContent from './NoteContent';
import FollowButton from './FollowButton';
import UserBadges from './UserBadges';
import VerifiedBadge from './VerifiedBadge';
import { useDisplayName } from '../hooks/useNip05Verified';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useNostrProfile, useNostrEvent, useNostrQuery, getCachedProfile } from '../hooks/useNostrQuery';
import { timeAgo } from '../lib/utils';
import BCHTipButton from './BCHTipButton';
import BoostPostModal from './BoostPostModal';
import { useBookmarks } from '../hooks/useBookmarks';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { FLAG_KIND, FLAG_TAG, FLAG_THRESHOLD, isFlagImmune, getFlaggers, isCommunityHidden, isBanned } from '../lib/communityFlag';
import { mightNeedTranslation, translateText, getLanguageName, type TranslationResult } from '../lib/translate';
import { uploadImage } from '../lib/imageUpload';
import { OPERATOR_PUBKEY, isDataSaverEnabled } from '../lib/platform';
import { MEMBERSHIP_KIND, membershipDTag, parseMembership, COMMUNITY_DEFINITION_KIND, COMMUNITY_POST_KIND, communityATag } from '../lib/communities';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { resolveTipTokens, findTipToken, findTipTokenByCategory, baseUnitsToWhole } from '../lib/tipTokens';

// Profile loading is now handled by the batched profile system in useNostrQuery
// No more IntersectionObserver needed — profiles are fetched in bulk

/** Compact number formatting for engagement counts, e.g. 1200 → "1.2K". */
function formatReach(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return (k >= 10 ? Math.round(k) : Math.round(k * 10) / 10) + 'K';
  }
  const m = n / 1_000_000;
  return (m >= 10 ? Math.round(m) : Math.round(m * 10) / 10) + 'M';
}

// ── Community membership gate ────────────────────────────────────────
// Detects if a post belongs to a community (has an 'A' tag pointing to
// a kind 34550 community definition) and checks if the current user is
// a member. Used to gate replies on community posts.

/** Extract the community a-tag from an event's tags (e.g. "34550:<pubkey>:<d-tag>") */
function getCommunityId(event: { tags: string[][] }): string | null {
  // Check uppercase 'A' tag first (NIP-72 community reference), then lowercase 'a'
  for (const t of event.tags) {
    if ((t[0] === 'A' || t[0] === 'a') && t[1]?.startsWith(`${COMMUNITY_DEFINITION_KIND}:`)) {
      return t[1];
    }
  }
  return null;
}

/** Lightweight hook: is the current user a member of the community this event belongs to?
 *  Returns { communityId, isMember, loading } — only fires a query if the event IS a community post. */
function useCommunityGate(event: { tags: string[][] } | null, userPubkey: string | undefined) {
  const communityId = event ? getCommunityId(event) : null;
  const dTag = communityId ? membershipDTag(communityId) : '';

  // Query the user's own membership event for this community
  const { events: memberEvents, loading } = useNostrQuery({
    filters: userPubkey && dTag ? [
      { kinds: [MEMBERSHIP_KIND], authors: [userPubkey], '#d': [dTag], limit: 1 },
    ] : [],
    enabled: !!userPubkey && !!dTag,
  });

  const isMember = useMemo(() => {
    if (!communityId || !userPubkey) return false;
    return memberEvents.some(ev => {
      const m = parseMembership(ev);
      return m && m.communityId === communityId && m.pubkey === userPubkey;
    });
  }, [memberEvents, communityId, userPubkey]);

  // Also check if user is the community creator (always has access)
  const isCreator = useMemo(() => {
    if (!communityId || !userPubkey) return false;
    // communityId format: "34550:<creator-pubkey>:<d-tag>"
    const parts = communityId.split(':');
    return parts.length >= 2 && parts[1] === userPubkey;
  }, [communityId, userPubkey]);

  return {
    communityId,
    isCommunityPost: !!communityId,
    isMember: isMember || isCreator,
    loading: !!communityId && loading,
  };
}

// Export for use in NotePage
export { getCommunityId, useCommunityGate };

// Stable since value — set once when module loads, shared by all PostCards
const TIP_SINCE = Math.floor(Date.now() / 1000) - 86400 * 7;

// ── Global deletion cache ────────────────────────────────────────
// Tracks event IDs that the current user has deleted (kind 5).
// Fetched once per session so PostCard can hide them without extra queries.
const _deletedIds = new Set<string>();
let _deletionsFetched = false;
let _deletionsFetchingFor: string | null = null;

export function fetchDeletionsForUser(pubkey: string) {
  if (_deletionsFetched && _deletionsFetchingFor === pubkey) return;
  _deletionsFetchingFor = pubkey;
  _deletionsFetched = true;
  _deletedIds.clear();

  // Query all relays for this user's kind 5 events
  const relays = getRelays();
  for (const url of relays) {
    try {
      const ws = new WebSocket(url);
      const subId = 'del_' + Math.random().toString(36).slice(2, 8);
      const timeout = setTimeout(() => { try { ws.close(); } catch {} }, 6000);

      ws.onopen = () => {
        ws.send(JSON.stringify(['REQ', subId, {
          kinds: [5],
          authors: [pubkey],
          limit: 500,
        }]));
      };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (data[0] === 'EVENT' && data[2]) {
            const ev = data[2];
            for (const tag of ev.tags || []) {
              if (tag[0] === 'e') _deletedIds.add(tag[1]);
            }
          }
          if (data[0] === 'EOSE') {
            clearTimeout(timeout);
            try { ws.close(); } catch {}
          }
        } catch {}
      };
      ws.onerror = () => clearTimeout(timeout);
    } catch {}
  }
}

/** Mark an event as deleted locally (called after publishing kind 5) */
export function markDeleted(eventId: string) {
  _deletedIds.add(eventId);
}

/** Check if an event has been deleted */
export function isDeletedEvent(eventId: string): boolean {
  return _deletedIds.has(eventId);
}

// ── NIP-32 Content Origin Badges ─────────────────────────────────
// Displays small badges for self-reported content labels:
// "Original Content 📸", "AI Generated 🤖", "Edited/Manipulated ✏️"
// Also supports bchnostr.content labels (ACTIVITY, DISCUSSION, etc.)
const BCHNOSTR_LABEL_STYLES: Record<string, { emoji: string; label: string; cls: string; title: string }> = {
  'ACTIVITY':     { emoji: '\u{1F3AF}', label: 'Activity',     cls: 'bg-primary/10 text-primary border-primary/20',             title: 'Community activity or challenge' },
  'DISCUSSION':   { emoji: '\u{1F4AC}', label: 'Discussion',   cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',  title: 'General discussion' },
  'QUESTION':     { emoji: '\u2753',     label: 'Question',     cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', title: 'Question for the community' },
  'ANNOUNCEMENT': { emoji: '\u{1F4E2}', label: 'Announcement', cls: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',      title: 'Important announcement' },
  'SHOWCASE':     { emoji: '✨',     label: 'Showcase',     cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', title: 'Showcasing work' },
};

function ContentOriginBadges({ tags }: { tags: string[][] }) {
  // Extract labels from the "content-origin" namespace (NIP-32 self-reporting)
  const labels = tags
    .filter(t => t[0] === 'l' && t[2] === 'content-origin')
    .map(t => t[1]);

  // Extract bchnostr.content labels
  const bchnostrLabels = tags
    .filter(t => t[0] === 'l' && t[2] === 'bchnostr.content')
    .map(t => t[1]);

  if (labels.length === 0 && bchnostrLabels.length === 0) return null;

  return (
    <>
      {labels.includes('original') && (
        <span className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-medium shrink-0 border border-emerald-500/20" title="Original content — taken by this creator">
          📸 OC
        </span>
      )}
      {labels.includes('ai-generated') && (
        <span className="text-[10px] bg-purple-500/10 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-medium shrink-0 border border-purple-500/20" title="AI-generated content">
          🤖 AI
        </span>
      )}
      {labels.includes('manipulated') && (
        <span className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-medium shrink-0 border border-amber-500/20" title="Edited or manipulated media">
          ✏️ Edited
        </span>
      )}
      {bchnostrLabels.map(lbl => {
        const style = BCHNOSTR_LABEL_STYLES[lbl];
        if (!style) return null;
        return (
          <span key={lbl} className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-medium shrink-0 border ${style.cls}`} title={style.title}>
            {style.emoji} {style.label}
          </span>
        );
      })}
    </>
  );
}

interface PostCardProps {
  event: Event;
  showReplyTo?: boolean;
  /** If true, show a "Pinned" badge on this post */
  isPinned?: boolean;
  /** Callback to pin this post (shown in menu for own posts / mod actions) */
  onPin?: () => void;
  /** Callback to unpin this post */
  onUnpin?: () => void;
}

// Memoize PostCard so it only re-renders when its own event changes.
// This prevents cascade re-renders when the parent feed re-renders due to
// new events, counter updates, or live data changes from other posts.
// Read once at module level — avoids reading localStorage per render
const _postCardDataSaver = isDataSaverEnabled();

const PostCard = React.memo(function PostCard({ event: rawEvent, showReplyTo = true, isPinned, onPin, onUnpin }: PostCardProps) {
  // Sanitize: ensure tags is always an array — malformed relay events can have undefined tags
  const event = rawEvent && !Array.isArray(rawEvent.tags)
    ? { ...rawEvent, tags: [] as string[][] }
    : rawEvent;

  const { user, isMuted, mute, unmute } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(event.pubkey);
  const displayName = useDisplayName(event.pubkey, profile, event.pubkey.slice(0, 8) + '...');
  // Community gate: if this post belongs to a community, only members can reply
  const { isCommunityPost, isMember: isCommunityMember, communityId } = useCommunityGate(event, user?.pubkey);
  const communityReplyBlocked = isCommunityPost && !isCommunityMember;
  const [showMenu, setShowMenu] = useState(false);
  const [confirmMute, setConfirmMute] = useState(false);
  const [deleted, setDeleted] = useState(() => isDeletedEvent(event.id));
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(event.content);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Track what's actually displayed — switches to edited content after a save
  const [displayContent, setDisplayContent] = useState(event.content);
  const [isEdited, setIsEdited] = useState(() =>
    event.tags?.some((t: string[]) => (t[0] === 'e' && t[3] === 'edit') || t[0] === '-edit-of') ?? false
  );
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [localLiked, setLocalLiked] = useState(false);
  const [localReposted, setLocalReposted] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [quoteSending, setQuoteSending] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);
  const [localReplyCount, setLocalReplyCount] = useState(0);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const replyFileRef = useRef<HTMLInputElement>(null);
  const [replyImages, setReplyImages] = useState<string[]>([]);
  const [replyUploading, setReplyUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [tokensResolved, setTokensResolved] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [showCrossPost, setShowCrossPost] = useState(false);
  const [showTipQR, setShowTipQR] = useState(false);
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const [bookmarking, setBookmarking] = useState(false);
  const bookmarked = isBookmarked(event.id);
  const [confirmFlag, setConfirmFlag] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [translation, setTranslation] = useState<TranslationResult | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);
  const showTranslateButton = useMemo(() => mightNeedTranslation(displayContent), [displayContent]);
  const [labelingActivity, setLabelingActivity] = useState(false);
  const [labeledActivity, setLabeledActivity] = useState(false);

  const navigate = useNavigate();
  const isOwnPost = user?.pubkey === event.pubkey;
  const isOperator = user?.pubkey === OPERATOR_PUBKEY;

  // Auto-focus the inline reply textarea when it opens
  useEffect(() => {
    if (replying && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replying]);

  // Navigate to note page when clicking on the post body area
  // Skip if clicking on interactive elements (links, buttons, images, inputs, etc.)
  const handlePostClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Walk up the DOM to check if we clicked on an interactive element
    let el: HTMLElement | null = target;
    while (el && el !== e.currentTarget) {
      const tag = el.tagName.toLowerCase();
      if (
        tag === 'a' || tag === 'button' || tag === 'input' || tag === 'textarea' ||
        tag === 'select' || tag === 'img' || tag === 'video' ||
        el.getAttribute('role') === 'button' ||
        el.classList.contains('no-navigate')
      ) {
        return; // Don't navigate — user clicked something interactive
      }
      el = el.parentElement;
    }
    // Don't navigate if user is selecting text
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;

    navigate(`/note/${event.id}`);
  }, [navigate, event.id]);

  // Fetch reactions (kind 7) and reposts (kind 6) — separate from replies so
  // we can use a higher limit for each without them competing for the same cap.
  const { events: reactions } = useNostrQuery({
    filters: [{ kinds: [7, 6], '#e': [event.id], limit: _postCardDataSaver ? 50 : 500 }],
    enabled: true,
  });

  // Fetch replies separately — use allRelays so comments from other clients are found.
  // Include kind 1111 so NIP-72 community comments count toward the reply total
  // (replies to community posts are published as community-tagged 1111 events).
  const { events: replyEvents } = useNostrQuery({
    filters: [{ kinds: [1, 1111], '#e': [event.id], limit: _postCardDataSaver ? 50 : 500 }],
    enabled: true,
    allRelays: !_postCardDataSaver,
  });

  // Fetch tip receipts for THIS specific post (tagged with #e)
  const { events: tipReceipts } = useNostrQuery({
    filters: [{ kinds: [9735], '#e': [event.id], limit: _postCardDataSaver ? 10 : 20, since: TIP_SINCE }],
    enabled: true,
  });

  // Fetch community flags for this post
  const { events: flagEvents, refetch: refetchFlags } = useNostrQuery({
    filters: [{ kinds: [FLAG_KIND], '#e': [event.id], limit: _postCardDataSaver ? 10 : 50 }],
    enabled: true,
  });

  const flaggers = useMemo(() => getFlaggers(flagEvents), [flagEvents]);
  const myFlagged = user ? flaggers.has(user.pubkey) : false;
  const hiddenByCommunity = isCommunityHidden(flagEvents, event.pubkey);
  const isAuthorImmune = isFlagImmune(event.pubkey);

  // Count from relay data
  const reactionEvents = reactions.filter(e => e.kind === 7);
  const likeCount = reactionEvents.length + (localLiked ? 1 : 0);
  const repostCount = reactions.filter(e => e.kind === 6).length + (localReposted ? 1 : 0);
  const replyCount = replyEvents.length;

  // Collect unique emoji reactions for display
  const reactionEmojis = useMemo(() => {
    const counts: Record<string, number> = {};
    reactionEvents.forEach(e => {
      const emoji = e.content && e.content !== '+' ? e.content : '❤\uFE0F';
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [reactionEvents.length]);

  // Tip social proof.
  // Receipts come in two flavours: BCH tips (an `amount` tag in sats) and
  // CashToken tips (FURU/PUSD/… — an `amount_token` tag + a `t:token-tip`
  // marker, NO `amount` tag). Summing only sats showed "0 sats" on posts
  // tipped purely in tokens. We now also track a USD total (from each
  // receipt's recorded `amount_usd`) and whether any token tip is present,
  // so mixed/token tips display a meaningful dollar figure.
  const tipCount = tipReceipts.length;
  // tokensResolved is included so the per-token icons/amounts recompute once
  // the allowlist finishes resolving.
  const { tipTotal, tipUsdTotal, hasTokenTip, tokenTips } = useMemo(
    () => computeTipSummary(tipReceipts),
    [tipReceipts, tokensResolved],
  );
  const tipperPubkeys = [...new Set(tipReceipts.map(e => e.pubkey))].slice(0, 3);

  // If this post has any token tip, make sure the tip-token allowlist is
  // resolved so we can show the token's name/icon + whole amount. Resolving
  // is cached module-wide; the state bump just re-runs the memo once ready.
  useEffect(() => {
    if (tokensResolved) return;
    const hasToken = tipReceipts.some(e =>
      e.tags.some(t => t[0] === 't' && t[1] === 'token-tip') ||
      e.tags.some(t => t[0] === 'amount_token'),
    );
    if (!hasToken) return;
    let alive = true;
    resolveTipTokens().then(() => { if (alive) setTokensResolved(true); }).catch(() => {});
    return () => { alive = false; };
  }, [tipReceipts, tokensResolved]);

  // ── Reach (honest "views" analog) ────────────────────────────────
  // Nostr has no impression event and there is no central server that
  // could count who *saw* a post — so a real X-style view count is
  // impossible to source truthfully in a decentralized client. (This app
  // already refuses to fabricate view numbers for video; see lib/video.ts.)
  //
  // Instead we show REACH: the number of distinct accounts that engaged
  // with the post — anyone who reacted, reposted, replied, or zapped it.
  // It's computed purely from events PostCard already fetches (no extra
  // relay traffic) and it never overstates itself: it's a floor on how
  // many people the post reached, labelled as "reach", not "views".
  const reachCount = useMemo(() => {
    const seen = new Set<string>();
    for (const e of reactions) seen.add(e.pubkey);      // likes (7) + reposts (6)
    for (const e of replyEvents) seen.add(e.pubkey);    // replies (1 / 1111)
    for (const e of tipReceipts) seen.add(e.pubkey);    // zaps / tips (9735)
    seen.delete(event.pubkey);                          // don't count the author
    return seen.size;
  }, [reactions, replyEvents, tipReceipts, event.pubkey]);

  // Check if current user already liked/reposted
  const alreadyLiked = localLiked || (user ? reactions.some(e => e.pubkey === user.pubkey) : false);
  const alreadyReposted = localReposted || (user ? reactions.some(e => e.kind === 6 && e.pubkey === user.pubkey) : false);

  // Check for poll content (kind 1068)
  const isPoll = event.kind === 1068;
  const pollOptions = isPoll ? event.tags.filter(t => t[0] === 'option').map((t, i) => ({
    id: t[1] || String(i),
    label: t[2] || t[1],
  })) : [];

  // NOTE: these useCallback hooks MUST stay above the early returns below.
  // React requires the same number of hooks on every render; when a user is
  // muted/banned the component returns null, so any hook placed AFTER that
  // return would be skipped and crash with React error #300.
  const handleReplyImageUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setReplyUploading(true);
    try {
      const url = await uploadImage(file);
      setReplyImages(prev => [...prev, url]);
    } catch (e) {
      console.error('Reply image upload failed:', e);
    } finally {
      setReplyUploading(false);
    }
  }, []);

  const handleReplyPaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (file) handleReplyImageUpload(file);
  }, [handleReplyImageUpload]);

  // ── Early returns: safe now that ALL hooks above have run ──────────
  if (isMuted(event.pubkey)) return null;
  if (isBanned(event.pubkey)) return null;

  // ── Determine if this post is a reply (not a quote-repost) ──────
  // A quote-repost has a 'q' tag whose value matches an 'e' tag.
  // Those 'e' tags are for the quoted note, NOT a thread parent.
  const quotedIds = new Set(event.tags.filter(t => t[0] === 'q').map(t => t[1]));
  const eTags = event.tags.filter(t => t[0] === 'e' && !quotedIds.has(t[1]));

  const replyToId =
    // Prefer explicitly-marked 'reply' tag
    eTags.find(t => t[3] === 'reply')?.[1]
    // Then 'root' only if it's the sole e-tag (shallow reply straight to root)
    || (eTags.length === 1 && eTags[0][3] === 'root' ? eTags[0][1] : undefined)
    // No markers: last e-tag is the reply target (NIP-10)
    || (eTags.length > 0 && !eTags.some(t => t[3] === 'mention') ? eTags[eTags.length - 1][1] : undefined);

  // Get the pubkey of who we're replying to (first p-tag)
  const replyToPubkey = event.tags.find(t => t[0] === 'p')?.[1];

  const images = extractImages(event);

  const handleReaction = async (emoji: string = '+') => {
    if (!user || alreadyLiked) return;
    setLocalLiked(true);
    setShowReactionPicker(false);
    await publish({
      kind: 7,
      content: emoji,
      tags: [['e', event.id], ['p', event.pubkey]],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  const handleRepost = async () => {
    if (!user || alreadyReposted) return;
    try {
      const relayHint = getRelays()[0] || '';
      await publish({
        kind: 6,
        content: JSON.stringify(event),
        tags: [['e', event.id, relayHint], ['p', event.pubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
      setLocalReposted(true);
    } catch (e) {
      console.error('Repost failed:', e);
    }
  };

  const handleQuote = async () => {
    if (!user || !quoteText.trim()) return;
    setQuoteSending(true);
    try {
      const npub = nip19.npubEncode(event.pubkey);
      await publish({
        kind: 1,
        content: `${quoteText}\n\nnostr:${nip19.noteEncode(event.id)}`,
        tags: [
          ['q', event.id],
          ['p', event.pubkey],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setQuoting(false);
      setQuoteText('');
    } catch (e) {
      console.error('Quote failed:', e);
    } finally {
      setQuoteSending(false);
    }
  };

  const handleInlineReply = async () => {
    if (!user || (!replyText.trim() && replyImages.length === 0)) return;
    setReplySending(true);
    try {
      const tags: string[][] = [];

      // If this post belongs to a community, reply as a NIP-72 community
      // comment (kind 1111) carrying the community tag, so the comment stays
      // scoped to the community (discoverable, countable, moderatable).
      // `communityId` is "34550:<creator>:<dTag>".
      const communityParts = isCommunityPost && communityId ? communityId.split(':') : null;
      const communityCreator = communityParts?.[1] || '';
      const communityDTag = communityParts ? communityParts.slice(2).join(':') : '';
      const asCommunityComment = !!(isCommunityPost && communityCreator && communityDTag);

      // NIP-10: determine root. If this post is itself a reply, find the root tag.
      const rootTag = event.tags.find((t: string[]) => t[0] === 'e' && t[3] === 'root');
      const rootId = rootTag ? rootTag[1] : event.id;
      // If the root is different from this event, add the root tag
      if (rootId !== event.id) {
        tags.push(['e', rootId, '', 'root']);
      }
      tags.push(['e', event.id, '', 'reply']);
      tags.push(['p', event.pubkey]);

      if (asCommunityComment) {
        const aTag = communityATag(communityCreator, communityDTag);
        tags.push(['A', aTag]);
        tags.push(['a', aTag]);
        tags.push(['K', String(COMMUNITY_DEFINITION_KIND)]);
        tags.push(['k', String(event.kind)]);
      } else {
        tags.push(['t', 'bch']);
        tags.push(['t', 'bitcoincash']);
      }

      // Add imeta tags for attached images
      replyImages.forEach(url => {
        tags.push(['imeta', `url ${url}`]);
      });

      // Build content: text + image URLs on separate lines
      const parts: string[] = [];
      if (replyText.trim()) parts.push(replyText.trim());
      replyImages.forEach(url => parts.push(url));
      const fullContent = parts.join('\n\n');

      await publish({
        kind: asCommunityComment ? COMMUNITY_POST_KIND : 1,
        content: fullContent,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setReplyText('');
      setReplyImages([]);
      setReplying(false);
      setReplySuccess(true);
      setLocalReplyCount(prev => prev + 1);
      setTimeout(() => setReplySuccess(false), 3000);
    } catch (e) {
      console.error('Reply failed:', e);
    } finally {
      setReplySending(false);
    }
  };

  const handleShare = () => {
    navigator.share?.({ url: `${window.location.origin}/note/${event.id}` })
      .catch(() => {
        navigator.clipboard.writeText(`${window.location.origin}/note/${event.id}`);
      });
  };

  // Label a post as ACTIVITY (NIP-32 kind 1985)
  const handleLabelActivity = async () => {
    if (!user || labelingActivity) return;
    setLabelingActivity(true);
    try {
      await publish({
        kind: 1985,
        content: '',
        tags: [
          ['L', 'bchnostr.content'],
          ['l', 'ACTIVITY', 'bchnostr.content'],
          ['e', event.id, '', 'labeling'],
          ['p', event.pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setLabeledActivity(true);
      setShowMenu(false);
    } catch (e) {
      console.error('Label failed:', e);
    } finally {
      setLabelingActivity(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !isOwnPost) return;
    if (!confirm('Delete this post? This publishes a deletion request to relays.')) return;
    try {
      await publish({
        kind: 5,
        content: 'Deleted by author',
        tags: [['e', event.id], ['k', String(event.kind)]],
        created_at: Math.floor(Date.now() / 1000),
      });
      markDeleted(event.id);
      setDeleted(true);
      setShowMenu(false);
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleFlag = async () => {
    if (!user || isOwnPost || isAuthorImmune) return;
    setFlagging(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'spam',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
          ['t', FLAG_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(refetchFlags, 1000);
    } catch (e) {
      console.error('Flag failed:', e);
    } finally {
      setFlagging(false);
      setConfirmFlag(false);
      setShowMenu(false);
    }
  };

  const handleUnflag = async () => {
    if (!user || !myFlagged) return;
    // Publish a deletion event for our flag
    const myFlag = flagEvents.find(e => e.pubkey === user.pubkey);
    if (!myFlag) return;
    try {
      await publish({
        kind: 5,
        content: 'Unflagged',
        tags: [['e', myFlag.id], ['k', String(FLAG_KIND)]],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(refetchFlags, 1000);
    } catch (e) {
      console.error('Unflag failed:', e);
    } finally {
      setShowMenu(false);
    }
  };

  const handleEdit = async () => {
    if (!user || !isOwnPost || !editContent.trim()) return;
    const trimmed = editContent.trim();
    if (trimmed === displayContent.trim()) {
      // Nothing actually changed
      setEditing(false);
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      // 1. Build tags for the new version — keep everything except old edit markers.
      // Use a custom '-' prefixed tag so it doesn't pollute relay '#e' queries
      // (which would cause the edited version to appear as a "reply" comment
      // under the original post).
      const newTags = event.tags.filter(t =>
        !(t[0] === 'e' && t[3] === 'edit') &&
        !(t[0] === '-edit-of')
      );
      // Reference the original for edit history using a non-queryable tag
      newTags.push(['-edit-of', event.id]);

      // 2. Publish the replacement event. Keeping the original created_at
      // preserves the post's position in the feed.
      //
      // IMPORTANT: We do NOT delete the original event. Deleting it would
      // orphan all boosts, reactions (likes), replies, and tip receipts that
      // reference the original event ID via their 'e' tags. Instead, the
      // feed logic hides the original from display using the editedOriginalIds
      // set (it detects the '-edit-of' tag on the new version). The original
      // stays on relays so all references remain valid.
      await publish({
        kind: event.kind,
        content: trimmed,
        tags: newTags,
        created_at: event.created_at,
      });

      // 3. Immediately update what's displayed — no need to wait for relay
      setDisplayContent(trimmed);
      setIsEdited(true);
      setEditing(false);
      setShowMenu(false);
    } catch (e) {
      console.error('Edit failed:', e);
      setEditError(e instanceof Error ? e.message : 'Edit failed. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  if (deleted) {
    return (
      <article className="border-b border-border px-4 py-3 text-center">
        <p className="text-xs text-muted-foreground italic flex items-center justify-center gap-1.5">
          <Trash2 className="w-3 h-3" /> This post has been deleted by the author.
        </p>
      </article>
    );
  }

  // Community-hidden post — show notice only to the author, hide from everyone else
  if (hiddenByCommunity) {
    if (isOwnPost) {
      return (
        <article className="border-b border-border px-4 py-3">
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-red-500/5 border border-red-500/20">
            <ShieldAlert className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-red-500">Your post was hidden by the community</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {flaggers.size} people flagged this post as spam. It is no longer visible in the feed.
                Only you can see this notice.
              </p>
            </div>
          </div>
        </article>
      );
    }
    // Hidden from everyone else — render nothing
    return null;
  }

  return (
    <article className="border-b border-border px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer" onClick={handlePostClick}>
      {showReplyTo && replyToId && (
        <ReplyContext replyToId={replyToId} replyToPubkey={replyToPubkey} />
      )}
      
      <div className="flex gap-3">
        {/* Avatar */}
        <Link to={`/profile/${event.pubkey}`} className="shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover hover:opacity-80 transition-opacity" loading="lazy" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary/60" />
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-0.5">
            <Link to={`/profile/${event.pubkey}`} className="font-semibold text-sm hover:underline truncate">
              {displayName}
            </Link>
            <UserBadges pubkey={event.pubkey} size="sm" />
            <FollowButton pubkey={event.pubkey} size="sm" />
            <span className="text-xs text-muted-foreground shrink-0">{timeAgo(event.created_at)}</span>

            {isPinned && (
              <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-medium">
                <Pin className="w-2.5 h-2.5" /> Pinned
              </span>
            )}
            
            {isPoll && (
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <BarChart3 className="w-3 h-3" /> Poll
              </span>
            )}

            {/* NIP-32 content origin labels */}
            <ContentOriginBadges tags={event.tags} />

            <div className="ml-auto relative">
              <button onClick={() => setShowMenu(!showMenu)} className="p-1 rounded hover:bg-muted">
                <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-8 w-52 bg-popover border border-border rounded-lg shadow-lg z-20 py-1" onMouseLeave={() => { setShowMenu(false); setConfirmMute(false); }}>
                  {/* Share & save actions */}
                  <button
                    onClick={() => { handleShare(); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                  >
                    <Share className="w-4 h-4" /> Share / Copy link
                  </button>
                  <button
                    onClick={() => { setShowCrossPost(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-purple-500"
                  >
                    <Share2 className="w-4 h-4" /> Cross-post to X / Bluesky…
                  </button>
                  {user && (
                    <button
                      onClick={async () => {
                        if (bookmarking) return;
                        setBookmarking(true);
                        try { await toggleBookmark(event.id); } catch {}
                        setBookmarking(false);
                        setShowMenu(false);
                      }}
                      disabled={bookmarking}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                    >
                      <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current text-primary' : ''}`} />
                      {bookmarked ? 'Remove bookmark' : 'Bookmark'}
                    </button>
                  )}
                  {!isOwnPost && (
                    <button
                      onClick={() => { setShowTipQR(true); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-emerald-500"
                    >
                      <QrCode className="w-4 h-4" /> Tip with BCH wallet
                    </button>
                  )}
                  {/* Label as Activity — operator only */}
                  {isOperator && (
                    <button
                      onClick={handleLabelActivity}
                      disabled={labelingActivity || labeledActivity}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${
                        labeledActivity ? 'text-emerald-500' : 'text-primary'
                      } disabled:opacity-50`}
                    >
                      {labelingActivity ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : labeledActivity ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Tag className="w-4 h-4" />
                      )}
                      {labeledActivity ? 'Labeled as Activity' : labelingActivity ? 'Labeling...' : 'Label as Activity'}
                    </button>
                  )}
                  <button
                    onClick={() => { navigator.clipboard.writeText(event.id); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                  >
                    <Copy className="w-4 h-4" /> Copy note ID
                  </button>

                  {/* Own post actions */}
                  {isOwnPost && (
                    <>
                      <div className="my-1 border-t border-border" />
                      {/* Pin / Unpin */}
                      {isPinned && onUnpin ? (
                        <button
                          onClick={() => { onUnpin(); setShowMenu(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-primary"
                        >
                          <Pin className="w-4 h-4" /> Unpin from profile
                        </button>
                      ) : !isPinned && onPin ? (
                        <button
                          onClick={() => { onPin(); setShowMenu(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        >
                          <Pin className="w-4 h-4" /> Pin to profile
                        </button>
                      ) : null}
                      <button
                        onClick={() => { setEditing(true); setEditContent(displayContent); setEditError(null); setShowMenu(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                      >
                        <Pencil className="w-4 h-4" /> Edit post
                      </button>
                      <button
                        onClick={handleDelete}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-red-500"
                      >
                        <Trash2 className="w-4 h-4" /> Delete post
                      </button>
                    </>
                  )}

                  {/* Flag as spam — with confirmation */}
                  {user && !isOwnPost && !isAuthorImmune && (
                    <>
                      <div className="my-1 border-t border-border" />
                      {myFlagged ? (
                        <button
                          onClick={handleUnflag}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                        >
                          <Flag className="w-4 h-4" /> Remove spam flag
                        </button>
                      ) : confirmFlag ? (
                        <div className="px-3 py-2 space-y-1.5">
                          <p className="text-xs text-muted-foreground">
                            Flag as spam? After {FLAG_THRESHOLD} flags from different users, this post will be hidden.
                          </p>
                          <div className="flex gap-1.5">
                            <button
                              onClick={handleFlag}
                              disabled={flagging}
                              className="flex-1 px-2 py-1.5 rounded-md bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 disabled:opacity-50"
                            >
                              {flagging ? 'Flagging...' : 'Flag it'}
                            </button>
                            <button
                              onClick={() => setConfirmFlag(false)}
                              className="flex-1 px-2 py-1.5 rounded-md border border-border text-xs hover:bg-muted"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmFlag(true)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                        >
                          <Flag className="w-4 h-4" /> Flag as spam
                        </button>
                      )}
                    </>
                  )}

                  {/* Mute — with confirmation */}
                  {user && !isOwnPost && (
                    <>
                      <div className="my-1 border-t border-border" />
                      {isMuted(event.pubkey) ? (
                        <button
                          onClick={() => { unmute(event.pubkey); setShowMenu(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-500/10 flex items-center gap-2 text-emerald-600"
                        >
                          <Volume2 className="w-4 h-4" /> Unmute user
                        </button>
                      ) : confirmMute ? (
                        <div className="px-3 py-2 space-y-1.5">
                          <p className="text-xs text-muted-foreground">Mute this user? Their posts will be hidden.</p>
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => { mute(event.pubkey); setShowMenu(false); setConfirmMute(false); }}
                              className="flex-1 px-2 py-1.5 rounded-md bg-red-500 text-white text-xs font-medium hover:bg-red-600"
                            >
                              Mute
                            </button>
                            <button
                              onClick={() => setConfirmMute(false)}
                              className="flex-1 px-2 py-1.5 rounded-md border border-border text-xs hover:bg-muted"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmMute(true)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                        >
                          <VolumeX className="w-4 h-4" /> Mute user
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Content / Edit Mode */}
          {editing ? (
            <div className="space-y-2 mt-1 no-navigate" onClick={e => e.stopPropagation()}>
              <textarea
                ref={editTextareaRef}
                value={editContent}
                onChange={e => {
                  setEditContent(e.target.value);
                  // Auto-grow
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setEditing(false); setEditContent(displayContent); setEditError(null); }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleEdit(); }
                }}
                className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none min-h-[80px] leading-relaxed"
                autoFocus
                onFocus={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              />
              {editError && (
                <p className="text-xs text-red-500 flex items-center gap-1.5">
                  <X className="w-3 h-3 shrink-0" /> {editError}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleEdit}
                  disabled={editSaving || !editContent.trim() || editContent.trim() === displayContent.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 transition-colors"
                >
                  {editSaving ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-3 h-3" />}
                  {editSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditing(false); setEditContent(displayContent); setEditError(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
                <span className={`text-[10px] ml-auto font-mono ${editContent.length > 260 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                  {editContent.length}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground">Tip: Ctrl+Enter to save · Esc to cancel</p>
            </div>
          ) : (
            <>
              <NoteContent content={displayContent} tags={event.tags} collapsible collapseHeight={420} />
              {isEdited && (
                <span className="text-[10px] text-muted-foreground/60 italic mt-0.5 inline-block">
                  (edited)
                </span>
              )}

              {/* Translation */}
              {translation && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Languages className="w-3 h-3 text-primary/60" />
                    <span className="text-[10px] text-muted-foreground">
                      Translated from {getLanguageName(translation.detectedLang)}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTranslation(null); }}
                      className="text-[10px] text-primary hover:underline ml-auto no-navigate"
                    >
                      Show original
                    </button>
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                    {translation.translatedText}
                  </div>
                </div>
              )}

              {/* Translate button */}
              {!translation && showTranslateButton && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (translating) return;
                    setTranslating(true);
                    setTranslateError(false);
                    try {
                      const result = await translateText(displayContent);
                      // Don't show translation if the detected language is the same as target
                      if (result.detectedLang !== result.targetLang) {
                        setTranslation(result);
                      }
                    } catch {
                      setTranslateError(true);
                      setTimeout(() => setTranslateError(false), 3000);
                    } finally {
                      setTranslating(false);
                    }
                  }}
                  className="no-navigate flex items-center gap-1 mt-1 text-[11px] text-primary/70 hover:text-primary transition-colors"
                >
                  <Languages className="w-3 h-3" />
                  {translating ? 'Translating...' : translateError ? 'Translation failed' : 'Translate post'}
                </button>
              )}
            </>
          )}

          {/* Quoted note */}
          <QuotedNote event={event} />

          {/* Images */}
          {images.length > 0 && (
            <div className={`mt-2 grid gap-2 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {images.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="rounded-lg max-h-80 w-full object-cover cursor-pointer hover:opacity-90 bg-muted"
                  loading="lazy"
                  decoding="async"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLightboxUrl(url); }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ))}
            </div>
          )}

          {/* Image Lightbox */}
          {lightboxUrl && (
            <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
          )}

          {/* Poll */}
          {isPoll && pollOptions.length > 0 && (
            <PollDisplay options={pollOptions} eventId={event.id} eventPubkey={event.pubkey} tags={event.tags} />
          )}

          {/* Actions */}
          <div className="flex items-center gap-1 mt-2 -ml-2 no-navigate">
            <button
              onClick={() => {
                if (!user) { navigate(`/note/${event.id}`); return; }
                if (communityReplyBlocked && communityId) {
                  // Non-member trying to reply to a community post — go to community
                  navigate(`/community/${encodeURIComponent(communityId)}`);
                  return;
                }
                setReplying(r => !r); if (quoting) setQuoting(false);
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                replying ? 'text-primary bg-primary/5'
                  : communityReplyBlocked ? 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/5'
                  : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
              }`}
              title={communityReplyBlocked ? 'Join community to reply' : undefined}
            >
              <MessageCircle className="w-4 h-4" />
              {(replyCount + localReplyCount) > 0 && <span>{replyCount + localReplyCount}</span>}
            </button>

            <button
              onClick={handleRepost}
              disabled={!user || alreadyReposted}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                alreadyReposted ? 'text-emerald-500' : 'text-muted-foreground hover:text-emerald-500 hover:bg-emerald-500/5'
              }`}
            >
              <Repeat2 className="w-4 h-4" />
              {repostCount > 0 && <span>{repostCount}</span>}
            </button>

            <button
              onClick={() => user && setQuoting(!quoting)}
              disabled={!user}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                quoting ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
              }`}
              title="Quote"
            >
              <Quote className="w-4 h-4" />
            </button>

            <div className="relative">
              <div className="flex items-center">
                {/* Main reaction button — tap for ❤\uFE0F */}
                <button
                  onClick={() => handleReaction('❤\uFE0F')}
                  disabled={!user || alreadyLiked}
                  className={`flex items-center gap-1 px-2 py-1 rounded-l-lg text-xs transition-colors ${
                    alreadyLiked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/5'
                  }`}
                >
                  {(() => {
                    const myReaction = reactions.find(e => e.kind === 7 && e.pubkey === user?.pubkey);
                    const emoji = myReaction?.content && myReaction.content !== '+' ? myReaction.content : null;
                    if (emoji && alreadyLiked) return <span className="text-sm">{emoji}</span>;
                    return <Heart className={`w-4 h-4 ${alreadyLiked ? 'fill-current' : ''}`} />;
                  })()}
                  {reactionEmojis.length > 0 && (
                    <span className="flex items-center gap-0.5">
                      {reactionEmojis.map(([emoji, count]) => (
                        <span key={emoji} className="text-[11px]">{emoji === '❤\uFE0F' ? '' : emoji}</span>
                      ))}
                      <span>{likeCount}</span>
                    </span>
                  )}
                  {reactionEmojis.length === 0 && likeCount > 0 && <span>{likeCount}</span>}
                </button>
                {/* Expand picker button */}
                {user && !alreadyLiked && (
                  <button
                    onClick={() => setShowReactionPicker(!showReactionPicker)}
                    className="px-1 py-1 rounded-r-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 text-[10px]"
                  >
                    ▾
                  </button>
                )}
              </div>

              {/* Emoji reaction picker */}
              {showReactionPicker && (
                <>
                  {/* Tap-away backdrop for mobile */}
                  <div className="fixed inset-0 z-20 sm:hidden" onClick={() => setShowReactionPicker(false)} />
                  <div className="absolute bottom-full left-0 mb-1 bg-popover border border-border rounded-2xl shadow-xl z-30 p-2 flex gap-1 flex-wrap max-w-[280px] sm:max-w-none sm:flex-nowrap"
                    onMouseLeave={() => setShowReactionPicker(false)}
                  >
                    {['❤\uFE0F', '\u{1F525}', '\u{1F4AF}', '\u{1F602}', '\u{1F622}', '\u{1F44D}', '\u{1F44E}', '\u{1F680}', '\u26A1', '\u{1F64F}'].map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(emoji)}
                        className="w-11 h-11 flex items-center justify-center rounded-xl hover:bg-muted text-2xl transition-transform hover:scale-110 active:scale-95 active:bg-muted"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Tip button + social proof */}
            <div className="flex items-center gap-0.5">
              <BCHTipButton recipientPubkey={event.pubkey} recipientProfile={profile} eventId={event.id} />
              {tipCount > 0 && (
                <TipSocialProof
                  tipTotal={tipTotal}
                  tipUsdTotal={tipUsdTotal}
                  hasTokenTip={hasTokenTip}
                  tokenTips={tokenTips}
                  tipperPubkeys={tipperPubkeys}
                />
              )}
            </div>

            {user && (
              <button
                onClick={() => setShowBoostModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 text-amber-600 hover:from-amber-500/20 hover:to-orange-500/20 hover:border-amber-500/50 text-xs font-medium transition-all"
                title="Boost this post"
              >
                <Rocket className="w-3.5 h-3.5" />
                <span>Boost</span>
              </button>
            )}

            {/* Reach — distinct accounts that engaged with this post.
                Pushed to the far right like X's view count. Only shown once
                there is at least one engager so empty posts stay clean. */}
            {reachCount > 0 && (
              <span
                className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground/70 shrink-0"
                title={`Reach — ${reachCount.toLocaleString()} ${reachCount === 1 ? 'account has' : 'accounts have'} engaged with this post (likes, reposts, replies and tips). Nostr has no way to count silent viewers, so this is a verified floor, not an impression count.`}
              >
                <Eye className="w-4 h-4 text-emerald-500 animate-pulse" />
                <span>{formatReach(reachCount)}</span>
              </span>
            )}
          </div>

          {/* Quote composer */}
          {quoting && (
            <div className="mt-3 space-y-2 border border-border rounded-lg p-3 no-navigate">
              {/* Quoted note preview */}
              <div className="rounded-lg bg-muted/50 border border-border p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  {profile?.picture && <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" loading="lazy" />}
                  <span className="text-[11px] font-medium">{profile?.display_name || profile?.name || event.pubkey.slice(0, 8)}</span>
                  <span className="text-[10px] text-muted-foreground">{timeAgo(event.created_at)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground line-clamp-3">{event.content.slice(0, 200)}</p>
              </div>

              <textarea
                value={quoteText}
                onChange={e => setQuoteText(e.target.value)}
                placeholder="Add your comment..."
                className="w-full bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none min-h-[50px]"
                rows={2}
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleQuote}
                  disabled={quoteSending || !quoteText.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {quoteSending ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Quote className="w-3 h-3" />}
                  {quoteSending ? 'Posting...' : 'Post Quote'}
                </button>
                <button
                  onClick={() => { setQuoting(false); setQuoteText(''); }}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Inline reply composer */}
          {replying && (
            <div className="mt-3 no-navigate">
              {/* Hidden file input */}
              <input
                ref={replyFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleReplyImageUpload(file);
                  e.target.value = '';
                }}
              />

              <div className="flex items-start gap-2">
                {user && (
                  <div className="shrink-0 mt-1">
                    {(() => {
                      const myProfile = getCachedProfile(user.pubkey);
                      return myProfile?.picture ? (
                        <img src={myProfile.picture} alt="" className="w-7 h-7 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-primary/60" />
                        </div>
                      );
                    })()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {/* Image preview strip */}
                  {(replyImages.length > 0 || replyUploading) && (
                    <div className="flex gap-2 mb-2 overflow-x-auto">
                      {replyImages.map((url, i) => (
                        <div key={i} className="relative shrink-0 group">
                          <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" loading="lazy" />
                          <button
                            onClick={() => setReplyImages(prev => prev.filter((_, j) => j !== i))}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {replyUploading && (
                        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-primary/30 flex items-center justify-center shrink-0">
                          <Loader2 className="w-4 h-4 text-primary animate-spin" />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-end gap-1.5 bg-muted rounded-2xl pl-3 pr-1.5 py-1.5 focus-within:ring-1 focus-within:ring-primary transition-all">
                    <textarea
                      ref={replyInputRef}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onPaste={handleReplyPaste}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleInlineReply(); }
                        if (e.key === 'Escape') { setReplying(false); setReplyText(''); setReplyImages([]); }
                      }}
                      onInput={e => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = 'auto';
                        target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                      }}
                      placeholder={`Reply to ${profile?.display_name || profile?.name || 'this post'}…`}
                      className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none resize-none min-h-[32px] max-h-[120px] py-1"
                      rows={1}
                    />
                    <button
                      onClick={() => replyFileRef.current?.click()}
                      disabled={replyUploading}
                      className="shrink-0 p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors mb-0.5"
                      title="Attach image"
                    >
                      {replyUploading
                        ? <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        : <ImageIcon className="w-4 h-4" />
                      }
                    </button>
                    <button
                      onClick={handleInlineReply}
                      disabled={replySending || (!replyText.trim() && replyImages.length === 0)}
                      className="shrink-0 p-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 disabled:hover:bg-primary transition-colors mb-0.5"
                    >
                      {replySending
                        ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <Send className="w-4 h-4" />
                      }
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1 px-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] text-muted-foreground">
                        <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[9px]">⌘</kbd>+<kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[9px]">↵</kbd> to send
                      </p>
                      {(replyCount + localReplyCount) > 0 && (
                        <Link to={`/note/${event.id}`} className="text-[10px] text-primary hover:underline font-medium">
                          View {replyCount + localReplyCount === 1 ? '1 reply' : `all ${replyCount + localReplyCount} replies`} →
                        </Link>
                      )}
                    </div>
                    <button
                      onClick={() => { setReplying(false); setReplyText(''); setReplyImages([]); }}
                      className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reply success indicator */}
          {replySuccess && !replying && (
            <div className="mt-2 flex items-center gap-1.5 text-xs no-navigate animate-in fade-in">
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-medium text-emerald-500">Reply posted!</span>
              <span className="text-muted-foreground">·</span>
              <Link to={`/note/${event.id}`} className="text-primary hover:underline font-medium">View thread →</Link>
            </div>
          )}
        </div>
      </div>

      {/* Boost Modal */}
      {showBoostModal && (
        <BoostPostModal eventId={event.id} onClose={() => setShowBoostModal(false)} />
      )}

      {/* Cross-post Modal */}
      {showCrossPost && (
        <CrossPostModal
          content={event.content}
          postUrl={`https://bchnostr.com/note/${event.id}`}
          onClose={() => setShowCrossPost(false)}
        />
      )}

      {/* QR Tip Modal — for non-users / external wallet holders */}
      {showTipQR && (
        <PostTipQRModal
          pubkey={event.pubkey}
          profile={profile}
          onClose={() => setShowTipQR(false)}
        />
      )}
    </article>
  );
}, (prevProps, nextProps) => {
  // Only re-render when the event ID changes (different post) or showReplyTo changes
  return prevProps.event.id === nextProps.event.id && prevProps.showReplyTo === nextProps.showReplyTo;
});

export default PostCard;

function PollDisplay({ options, eventId, eventPubkey, tags }: { options: { id: string; label: string }[]; eventId: string; eventPubkey: string; tags: string[][] }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [optimisticVote, setOptimisticVote] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const pollType = tags.find(t => t[0] === 'polltype')?.[1] || 'single';
  const closedAt = tags.find(t => t[0] === 'closed_at')?.[1];
  const isExpired = closedAt ? parseInt(closedAt) < Date.now() / 1000 : false;

  // ── Fetch all votes (kind 1018) for this poll from relays ─────
  const { events: voteEvents, loading: votesLoading } = useNostrQuery({
    filters: [{ kinds: [1018], '#e': [eventId], limit: 500 }],
    enabled: !!eventId,
  });

  // ── Tally votes: one vote per pubkey (last vote wins) ─────────
  const { tally, myVotedOption } = useMemo(() => {
    // Per-pubkey: keep latest vote event
    const latestByPubkey = new Map<string, any>();
    voteEvents.forEach(ev => {
      const existing = latestByPubkey.get(ev.pubkey);
      if (!existing || ev.created_at > existing.created_at) {
        latestByPubkey.set(ev.pubkey, ev);
      }
    });

    const tally: Record<string, number> = {};
    let myVotedOption: string | null = null;

    latestByPubkey.forEach((ev, pubkey) => {
      const responseTag = ev.tags.find((t: string[]) => t[0] === 'response')?.[1];
      if (!responseTag) return;
      // Only count votes for valid option IDs
      if (!options.some(o => o.id === responseTag)) return;
      tally[responseTag] = (tally[responseTag] || 0) + 1;
      if (user && pubkey === user.pubkey) {
        myVotedOption = responseTag;
      }
    });

    return { tally, myVotedOption };
  }, [voteEvents, user, options]);

  // Merge optimistic vote into tally so UI updates instantly on click
  const displayTally = useMemo(() => {
    if (!optimisticVote || myVotedOption) return tally;
    return { ...tally, [optimisticVote]: (tally[optimisticVote] || 0) + 1 };
  }, [tally, optimisticVote, myVotedOption]);

  const voted = myVotedOption || optimisticVote;
  const totalVotes = Object.values(displayTally).reduce((a, b) => a + b, 0);
  const showBar = showResults || !!voted || isExpired;

  const handleVote = async (optionId: string) => {
    if (!user || voted || isExpired) return;
    // Optimistic update — show result bar immediately
    setOptimisticVote(optionId);
    setShowResults(true);
    await publish({
      kind: 1018,
      content: '',
      tags: [['e', eventId], ['p', eventPubkey], ['response', optionId]],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  return (
    <div className="mt-3 space-y-2">
      {options.map(opt => {
        const count = displayTally[opt.id] || 0;
        const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
        const isMyVote = voted === opt.id;

        return (
          <button
            key={opt.id}
            onClick={() => handleVote(opt.id)}
            disabled={!!voted || isExpired || !user}
            className={`relative w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all overflow-hidden ${
              isMyVote
                ? 'border-primary bg-primary/5'
                : voted || isExpired
                  ? 'border-border opacity-80 cursor-default'
                  : 'border-border hover:border-primary/50'
            }`}
          >
            {showBar && (
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-500 ${isMyVote ? 'bg-primary/15' : 'bg-muted'}`}
                style={{ width: `${pct}%` }}
              />
            )}
            <span className="relative z-10 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                {isMyVote && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                {opt.label}
              </span>
              {showBar && (
                <span className="text-xs text-muted-foreground font-mono shrink-0">
                  {pct}%
                </span>
              )}
            </span>
          </button>
        );
      })}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {votesLoading && totalVotes === 0
            ? 'Loading votes…'
            : `${totalVotes} vote${totalVotes !== 1 ? 's' : ''}`}
        </span>
        <div className="flex items-center gap-2">
          {isExpired && <span className="text-red-500">Poll ended</span>}
          {!showResults && !voted && !isExpired && (
            <button onClick={() => setShowResults(true)} className="text-primary hover:underline">
              Show results
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuotedNote({ event }: { event: Event }) {
  const qTag = event.tags.find(t => t[0] === 'q');
  const quotedId = qTag?.[1];
  // allRelays: the quoted note may live outside the default relay subset.
  const { event: quotedEvent } = useNostrEvent(quotedId, true);
  const { profile: quotedProfile } = useNostrProfile(quotedEvent?.pubkey);
  const quotedName = useDisplayName(
    quotedEvent?.pubkey,
    quotedProfile,
    quotedEvent ? quotedEvent.pubkey.slice(0, 8) : '',
  );

  if (!quotedId || !quotedEvent) return null;

  return (
    <Link to={`/note/${quotedId}`} className="mt-2 block rounded-lg border border-border bg-muted/30 p-3 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-1.5 mb-1">
        {quotedProfile?.picture && <img src={quotedProfile.picture} alt="" className="w-4 h-4 rounded-full object-cover" loading="lazy" />}
        <span className="text-[11px] font-medium">{quotedName}</span>
        <VerifiedBadge nip05={quotedProfile?.nip05} pubkey={quotedEvent.pubkey} size="xs" />
        <span className="text-[10px] text-muted-foreground">{timeAgo(quotedEvent.created_at)}</span>
      </div>
      <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{quotedEvent.content.slice(0, 280)}</p>
    </Link>
  );
}

function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Full-size image */}
      <img
        src={url}
        alt=""
        className="max-w-full max-h-[90vh] object-contain rounded-lg"
        onClick={e => e.stopPropagation()}
      />

      {/* Open in new tab */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs hover:bg-black/70 transition-colors flex items-center gap-1.5"
      >
        <Share className="w-3 h-3" /> Open Original
      </a>
    </div>
  );
}

function extractImages(event: Event): string[] {
  const urls: string[] = [];
  const videoExts = /\.(mp4|webm|mov|avi|mkv|m3u8)(\?.*)?$/i;
  // Only extract images from imeta tags that are NOT already in the content.
  // NoteContent already renders all media URLs found in the content text,
  // so we only need the grid for images that exist in tags but not in content.
  event.tags.filter(t => t[0] === 'imeta').forEach(t => {
    const mimeTag = t.find(v => v.startsWith('m '));
    if (mimeTag && mimeTag.includes('video')) return;
    const url = t.find(v => v.startsWith('url '));
    if (url) {
      const u = url.slice(4);
      if (videoExts.test(u)) return;
      if (event.content.includes(u)) return;
      if (!urls.includes(u)) urls.push(u);
    }
  });
  return urls;
}

/**
 * ReplyContext — shows who you're replying to with a preview of the parent note.
 * Makes conversation threads easy to follow in the main feed.
 */
function TipperAvatar({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return profile?.picture ? (
    <img src={profile.picture} alt="" className="w-5 h-5 rounded-full object-cover border-2 border-background" loading="lazy" title={profile.name || pubkey.slice(0, 8)} />
  ) : (
    <div className="w-5 h-5 rounded-full bg-emerald-500/20 border-2 border-background flex items-center justify-center">
      <Coins className="w-2.5 h-2.5 text-emerald-500" />
    </div>
  );
}

// ── Tip Social Proof ──────────────────────────────────────────────
// Shows tipper avatars + the amount earned on a post. Handles BCH tips
// (summed in sats) and CashToken tips (FURU/PUSD/…), which carry no sats
// amount — for those we fall back to the USD total recorded on each
// receipt, so token-tipped posts no longer read "0 sats".

export interface TokenTipSummary {
  symbol: string;
  iconUrl?: string;
  stable: boolean;
  /** Whole-token amount, or null when decimals are unknown. */
  whole: number | null;
  usd: number;
}

export interface TipSummary {
  tipTotal: number;
  tipUsdTotal: number;
  hasTokenTip: boolean;
  tokenTips: TokenTipSummary[];
}

/**
 * Compute a tip summary from an array of kind-9735 tip-receipt events.
 *
 * Handles both BCH tips (an `amount` tag in sats) and CashToken tips
 * (FURU/PUSD/… — an `amount_token` tag + a `t:token-tip` marker, no `amount`).
 * Reused by PostCard and NotePage so token tips render identically — and so
 * token-only posts never read "0 sats".
 *
 * Call `resolveTipTokens()` once before relying on token icons/amounts; the
 * allowlist is module-cached, so an unresolved token simply shows its ticker.
 */
export function computeTipSummary(tipReceipts: Event[]): TipSummary {
  let sats = 0;
  let usd = 0;
  let tokenSeen = false;
  const tokens = new Map<string, { symbol: string; category?: string; base: bigint; usd: number }>();

  for (const e of tipReceipts) {
    const amountTag = e.tags.find(t => t[0] === 'amount');
    if (amountTag) {
      const n = parseInt(amountTag[1]);
      if (Number.isFinite(n)) sats += n;
    }
    const isToken = e.tags.some(t => t[0] === 't' && t[1] === 'token-tip')
      || e.tags.some(t => t[0] === 'amount_token');

    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(e.content || '{}'); } catch { /* non-JSON receipt */ }
    const receiptUsd = typeof parsed.amount_usd === 'number' && parsed.amount_usd > 0 ? parsed.amount_usd : 0;
    if (receiptUsd > 0) usd += receiptUsd;

    if (isToken) {
      tokenSeen = true;
      const symbol = (e.tags.find(t => t[0] === 'currency')?.[1]
        || (typeof parsed.currency === 'string' ? parsed.currency : '')
        || 'token').toUpperCase();
      const category = e.tags.find(t => t[0] === 'token')?.[1]
        || (typeof parsed.category === 'string' ? parsed.category : undefined);
      const rawAmt = e.tags.find(t => t[0] === 'amount_token')?.[1]
        ?? (parsed.amount_token != null ? String(parsed.amount_token) : undefined);
      let base = BigInt(0);
      if (rawAmt && /^\d+$/.test(rawAmt)) { try { base = BigInt(rawAmt); } catch { /* ignore */ } }

      const key = category?.toLowerCase() || symbol;
      const prev = tokens.get(key);
      if (prev) {
        prev.base += base;
        prev.usd += receiptUsd;
      } else {
        tokens.set(key, { symbol, category, base, usd: receiptUsd });
      }
    }
  }

  const list = [...tokens.values()]
    .map(t => {
      const resolved = (t.category ? findTipTokenByCategory(t.category) : null) ?? findTipToken(t.symbol);
      const decimals = resolved?.decimals ?? 0;
      const whole = decimals > 0 ? baseUnitsToWhole(t.base, decimals) : Number(t.base);
      return {
        symbol: t.symbol,
        iconUrl: resolved?.iconUrl || undefined,
        stable: resolved?.stable ?? false,
        whole: resolved ? whole : null,
        usd: t.usd,
      };
    })
    .sort((a, b) => b.usd - a.usd);

  return { tipTotal: sats, tipUsdTotal: usd, hasTokenTip: tokenSeen, tokenTips: list };
}

/** Format one token's whole amount, e.g. "13 FURU" or "$2.00 PUSD". */
function formatTokenTip(t: TokenTipSummary): string {
  if (t.stable && t.whole != null) return `$${t.whole.toFixed(2)} ${t.symbol}`;
  if (t.whole == null) return t.symbol; // decimals unknown — just the ticker
  return `${t.whole.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${t.symbol}`;
}

export function TipSocialProof({
  tipTotal,
  tipUsdTotal = 0,
  hasTokenTip = false,
  tokenTips = [],
  tipperPubkeys,
}: {
  tipTotal: number;
  tipUsdTotal?: number;
  hasTokenTip?: boolean;
  tokenTips?: TokenTipSummary[];
  tipperPubkeys: string[];
}) {
  const bchPrice = getBCHPrice();
  const bch = tipTotal / 1e8;
  const bchUsd = bchPrice && bchPrice > 0 ? bch * bchPrice : 0;
  // Combined dollar value across BCH + token tips (token tips contribute via
  // their recorded amount_usd; tipUsdTotal already includes BCH tips that
  // recorded a USD value, so prefer the larger of the two estimates).
  const usd = Math.max(tipUsdTotal, bchUsd);

  const hasBchTip = tipTotal > 0;

  // ── Main amount label ──────────────────────────────────────────
  // Priority: name the token when we can (most informative), else dollars,
  // else the familiar sats/BCH for pure-BCH tips.
  let amountStr: string;
  if (hasTokenTip && !hasBchTip && tokenTips.length === 1) {
    // Single token, no BCH — show the actual token amount, e.g. "13 FURU".
    amountStr = formatTokenTip(tokenTips[0]);
  } else if (hasTokenTip) {
    // Mixed BCH + token, or several tokens — lead with the dollar total
    // (token icons rendered alongside name which tokens were involved).
    amountStr = usd >= 0.01 ? `$${usd.toFixed(2)}` : 'tipped';
  } else if (tipTotal === 0) {
    amountStr = usd >= 0.01 ? `$${usd.toFixed(2)}` : 'tipped';
  } else if (bch >= 0.01) {
    amountStr = `${bch.toFixed(4)} BCH`;
  } else if (bch >= 0.001) {
    amountStr = `${bch.toFixed(6)} BCH`;
  } else {
    amountStr = tipTotal >= 1e6
      ? `${(tipTotal / 1e6).toFixed(1)}M sats`
      : tipTotal >= 1e3
        ? `${(tipTotal / 1e3).toFixed(1)}k sats`
        : `${tipTotal} sats`;
  }

  // Token icons — shown whenever tokens were tipped, so the currency is
  // visible at a glance even when the label is a dollar figure.
  const tokenIcons = tokenTips.filter(t => t.iconUrl).slice(0, 3);

  const title = hasTokenTip
    ? `Tipped ${tokenTips.map(formatTokenTip).join(', ')}${hasBchTip ? ` + ${tipTotal.toLocaleString()} sats` : ''}${usd >= 0.01 ? ` (~$${usd.toFixed(2)} USD)` : ''}`
    : `${tipTotal.toLocaleString()} sats total${usd >= 0.01 ? ` (~$${usd.toFixed(2)} USD)` : ''}`;

  // Append the "(...)" USD hint when the main label isn't already a dollar
  // figure and USD adds info: pure-BCH tips, or a single-token amount.
  const labelIsDollars = amountStr.startsWith('$');
  const showUsdHint = usd >= 0.01 && !labelIsDollars && (hasBchTip || (hasTokenTip && !tokenTips[0]?.stable));

  // Name the token(s) tipped so the chip reads e.g. "13 FURU" at a glance.
  // For a single non-dollar token tip the amount label already carries the
  // symbol; otherwise append a compact symbol list next to the icons.
  const tokenSymbols = tokenTips.map(t => t.symbol);
  const symbolSuffix =
    hasTokenTip && labelIsDollars && tokenSymbols.length > 0
      ? tokenSymbols.slice(0, 2).join(' · ') + (tokenSymbols.length > 2 ? '…' : '')
      : '';

  return (
    <div
      className="no-navigate inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 pl-1 pr-2 py-0.5 text-emerald-600 dark:text-emerald-400 text-xs shadow-sm shadow-emerald-500/5"
      title={title}
    >
      {/* Tipper avatars */}
      <div className="flex -space-x-1.5">
        {tipperPubkeys.map(pk => (
          <TipperAvatar key={pk} pubkey={pk} />
        ))}
      </div>

      {/* Token icon(s) — larger and ringed so the currency reads clearly.
          Falls back to a coin glyph for pure-BCH tips. */}
      {tokenIcons.length > 0 ? (
        <div className="flex -space-x-1.5">
          {tokenIcons.map(t => (
            <img
              key={t.symbol}
              src={t.iconUrl}
              alt={t.symbol}
              title={formatTokenTip(t)}
              className="w-4 h-4 rounded-full ring-2 ring-emerald-500/15 bg-background object-cover"
              loading="lazy"
            />
          ))}
        </div>
      ) : (
        <Coins className="w-3.5 h-3.5 shrink-0" />
      )}

      {/* Amount */}
      <span className="font-bold tabular-nums leading-none">{amountStr}</span>
      {symbolSuffix && (
        <span className="font-semibold text-[10px] text-emerald-500/80 leading-none">{symbolSuffix}</span>
      )}
      {showUsdHint && (
        <span className="text-emerald-500/60 dark:text-emerald-400/60 font-medium text-[10px] leading-none">≈ ${usd.toFixed(2)}</span>
      )}
    </div>
  );
}

// ── Post QR Tip Modal ─────────────────────────────────────────────
// Lets anyone (no login needed) tip a post author via their BCH wallet.

function PostTipQRModal({ pubkey, profile, onClose }: {
  pubkey: string;
  profile: any;
  onClose: () => void;
}) {
  const { address, loading } = useBCHAddress(pubkey);
  const [copied, setCopied] = useState(false);

  const displayName = useDisplayName(pubkey, profile, pubkey.slice(0, 10) + '…');
  const displayAddr = address?.replace(/^bitcoincash:/i, '') ?? null;
  const qrUrl = address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&format=png&data=${encodeURIComponent(address)}`
    : null;

  // Lock background scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleCopy = () => {
    if (!address) return;
    navigator.clipboard.writeText(address).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-xs rounded-t-2xl sm:rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2.5">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" loading="lazy" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary/60" />
              </div>
            )}
            <div>
              <p className="text-sm font-bold leading-tight">Tip {displayName}</p>
              <p className="text-[11px] text-muted-foreground">Scan with any BCH wallet</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 flex flex-col items-center gap-3">
          {loading ? (
            <div className="w-[180px] h-[180px] flex items-center justify-center">
              <div className="w-7 h-7 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          ) : !address ? (
            <div className="w-[180px] h-[180px] flex flex-col items-center justify-center gap-2 text-center">
              <QrCode className="w-10 h-10 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground px-4">
                {displayName} hasn't set up a BCH address yet.
              </p>
            </div>
          ) : (
            <>
              {/* QR code */}
              <div className="w-[180px] h-[180px] rounded-xl bg-white p-2 border border-border flex items-center justify-center overflow-hidden">
                <img
                  src={qrUrl!}
                  alt={`BCH address QR for ${displayName}`}
                  className="w-full h-full object-contain"
                  loading="eager"
                />
              </div>

              {/* Address */}
              <div className="w-full rounded-xl bg-muted/50 px-3 py-2.5">
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">BCH Address</p>
                <p className="text-[11px] font-mono text-foreground break-all leading-relaxed select-all">
                  {displayAddr}
                </p>
              </div>

              {/* Copy button */}
              <button
                onClick={handleCopy}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] ${
                  copied
                    ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Address'}
              </button>
            </>
          )}

          <p className="text-[10px] text-muted-foreground text-center leading-relaxed pb-1">
            Open your Bitcoin Cash wallet · tap Send · scan or paste the address above
          </p>
        </div>
      </div>
    </div>
  );
}

function ReplyContext({ replyToId, replyToPubkey }: { replyToId: string; replyToPubkey?: string }) {
  const { event: parentEvent } = useNostrEvent(replyToId);
  const parentPubkey = replyToPubkey || parentEvent?.pubkey;
  const { profile: parentProfile } = useNostrProfile(parentPubkey);

  const parentAuthor = useDisplayName(
    parentPubkey,
    parentProfile,
    (parentPubkey || '').slice(0, 8) + '...',
  );

  // Truncate parent content for preview
  const parentPreview = parentEvent?.content
    ? parentEvent.content
        .replace(/(https?:\/\/\S+)/g, '[link]') // collapse URLs
        .replace(/nostr:\S+/g, '@user') // collapse nostr mentions
        .slice(0, 120) + (parentEvent.content.length > 120 ? '...' : '')
    : null;

  return (
    <Link
      to={`/note/${replyToId}`}
      className="mb-2 flex items-start gap-2 rounded-lg bg-muted/40 border border-border/50 px-3 py-2 hover:bg-muted/60 transition-colors group"
    >
      <CornerDownRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          {parentProfile?.picture && (
            <img src={parentProfile.picture} alt="" className="w-4 h-4 rounded-full object-cover" loading="lazy" />
          )}
          <span className="text-xs font-medium text-primary truncate">
            Replying to {parentAuthor}
          </span>
          <VerifiedBadge
            nip05={parentProfile?.nip05}
            pubkey={replyToPubkey || parentEvent?.pubkey}
            size="xs"
          />
        </div>
        {parentPreview ? (
          <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">{parentPreview}</p>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">Loading original post...</p>
        )}
      </div>
    </Link>
  );
}

